(ns practitest.frontend.integration-settings.jira-entity-mapping.events
  (:require [re-frame.core :as re-frame]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.components.notifications :as notifications]
            [ajax.core :as ajax]))

(defn- ids-for-entity
  "Get all type IDs for an entity type (requirements or issues) from a cached project's types."
  [types-for-project entity-type]
  (mapv :id (get types-for-project entity-type [])))

(defn- build-type-names
  "Build {type-id-string -> {:name :is_sub_task}} from a cached project's types."
  [types-for-project]
  (let [all-types (concat (get types-for-project :requirements [])
                          (get types-for-project :issues []))]
    (reduce (fn [acc {:keys [id name subtask]}]
              (assoc acc (str id) {:name name :is_sub_task subtask}))
            {}
            all-types)))

(defn- apply-filtered-defaults-single-project
  "Compute default jira_type for one project's entity type, excluding types selected on the
   other entity. Reads types from the per-project cache. If the cache is missing for that
   project, returns the existing entity unchanged so we don't overwrite saved selections
   while a fetch is still in flight."
  [project-id current-mappings project-issue-types entity-type other-entity-enabled?]
  (let [pid-key (keyword project-id)
        types (get project-issue-types project-id)
        existing (get-in current-mappings [pid-key entity-type])]
    (if-not types
      (or existing {:jira_type []})
      (let [other-entity-type (if (= entity-type :requirements) :issues :requirements)
            other-map (get-in current-mappings [pid-key other-entity-type])
            other-disabled? (:disabled other-map false)
            other-selected (if (or other-disabled? (not other-entity-enabled?))
                             #{}
                             (set (:jira_type other-map [])))
            project-types (ids-for-entity types entity-type)]
        {:jira_type (vec (remove #(contains? other-selected %) project-types))}))))

(defn- apply-filtered-defaults
  [current-mappings project-issue-types entity-type other-entity-enabled?]
  (reduce-kv
    (fn [acc pid-key mapping]
      (assoc acc pid-key
             (assoc mapping entity-type
                    (apply-filtered-defaults-single-project (name pid-key)
                                                            current-mappings
                                                            project-issue-types
                                                            entity-type
                                                            other-entity-enabled?))))
    {}
    current-mappings))

;; NOTE: this is not a great fix, but it should do for now
;;       `entity_mapping_issue_types` can be slow. 
;;       Serializing requests prevents spikes in these requests
(defn- chain-next-fetch [db]
  (let [selected (get-in db [:entity-mapping :selected-project-ids] #{})
        cached (get-in db [:entity-mapping :project-issue-types] {})
        loading (get-in db [:entity-mapping :project-types-loading] #{})
        failed (get-in db [:entity-mapping :project-types-failed] #{})]
    (when-let [pid (->> selected
                        (map name)
                        (remove #(contains? cached %))
                        (remove #(contains? loading %))
                        (remove #(contains? failed %))
                        (first))]
      [[:dispatch [::fetch-project-issue-types pid]]])))

;; -----------------------------------------------------------------------------
;; Initialization
;; -----------------------------------------------------------------------------

(re-frame/reg-event-db
 ::initialize-db
 (fn [db [_ {:keys [project-id integration-id bugtracker-label identifier tabs active-tab]}]]
   (assoc db :entity-mapping
          {:project-id            project-id
           :integration-id        integration-id
           :bugtracker-label      bugtracker-label
           :identifier            identifier
           :tabs                  tabs
           :active-tab            active-tab
           :loading?              true
           :projects-loading?     true
           :selected-project-ids  #{}
           :available-projects    []
           :project-issue-types   {}
           :project-types-loading #{}
           :project-types-failed #{}
           :data                  nil
           :errors                []})))

;; -----------------------------------------------------------------------------
;; Settings (entity_mapping endpoint) — fast, DB-only
;; -----------------------------------------------------------------------------

(re-frame/reg-event-fx
 ::fetch-entity-mapping
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)]
     {:http-xhrio {:method          :get
                   :uri             (broutes/get-url "integration_settings#entity_mapping"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-entity-mapping-success]
                   :on-failure      [::fetch-entity-mapping-failure]}})))

(re-frame/reg-event-fx
  ::fetch-entity-mapping-success
  (fn [{:keys [db]} [_ response]]
    (let [project-mappings (:project_mappings response {})
          mapped-pids (keys project-mappings)
          db' (update db :entity-mapping merge
                      {:loading? false
                       :data response
                       :saved-data response
                       :selected-project-ids (set mapped-pids)
                       :saved-selected-project-ids (set mapped-pids)
                       :errors []})]
      {:db db'
       ;; Refresh type names serially in the background (see chain-next-fetch)
       :fx (chain-next-fetch db')})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "bugtracker.integration-settings.error.fetch-failed"
                                                "Failed to load entity mapping"))]
     {:db       (update db :entity-mapping merge {:loading? false})
      :dispatch [::notifications/error2 error-message]})))

;; -----------------------------------------------------------------------------
;; Available Jira projects (basic list, lazy)
;; -----------------------------------------------------------------------------

(re-frame/reg-event-fx
 ::fetch-available-projects
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)]
     {:db         (assoc-in db [:entity-mapping :projects-loading?] true)
      :http-xhrio {:method          :get
                   :uri             (broutes/get-url "integration_settings#available_jira_projects"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-available-projects-success]
                   :on-failure      [::fetch-available-projects-failure]}})))

(re-frame/reg-event-db
 ::fetch-available-projects-success
 (fn [db [_ response]]
   (update db :entity-mapping merge
           {:projects-loading?   false
            :available-projects  (vec (:available_projects response []))})))

(re-frame/reg-event-fx
 ::fetch-available-projects-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (format-translation (get-translation "bugtracker.integration-settings.error.projects-fetch-failed"
                                                                    "Failed to load %{bugtracker} projects")
                                                   :bugtracker "Jira"))]
     {:db       (assoc-in db [:entity-mapping :projects-loading?] false)
      :dispatch [::notifications/error2 error-message]})))

;; -----------------------------------------------------------------------------
;; Per-project issue types (categorized, lazy)
;; -----------------------------------------------------------------------------

(re-frame/reg-event-fx
 ::fetch-project-issue-types
 (fn [{:keys [db]} [_ jira-project-id]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)
         loading-set     (get-in db [:entity-mapping :project-types-loading] #{})
         types-cache     (get-in db [:entity-mapping :project-issue-types] {})
         already-active? (or (contains? loading-set jira-project-id)
                             (contains? types-cache jira-project-id))]
     (if already-active?
       {}
       {:db         (update-in db [:entity-mapping :project-types-loading] (fnil conj #{}) jira-project-id)
        :http-xhrio {:method          :get
                     :uri             (broutes/get-url "integration_settings#entity_mapping_issue_types"
                                                       {:project_id project-id
                                                        :id         integration-id})
                     :params          {:jira_project_id jira-project-id}
                     :response-format (ajax/json-response-format {:keywords? true})
                     :on-success      [::fetch-project-issue-types-success jira-project-id]
                     :on-failure      [::fetch-project-issue-types-failure jira-project-id]}}))))

(defn- merge-types-into-mapping
  "Refresh type_names from a fetched project's types. If the mapping was just added
   and is awaiting defaults, populate jira_type for both entities and clear the flag.
   Saved selections are otherwise preserved."
  [mapping types-for-project project-name]
  (let [base (cond-> (or mapping {})
               true                          (assoc :type_names (build-type-names types-for-project))
               (nil? (:project_name mapping)) (assoc :project_name project-name))]
    (if (:_pending_defaults? mapping)
      (-> base
          (assoc-in [:requirements :jira_type] (ids-for-entity types-for-project :requirements))
          (assoc-in [:issues :jira_type]       (ids-for-entity types-for-project :issues))
          (dissoc :_pending_defaults?))
      base)))

(re-frame/reg-event-fx
  ::fetch-project-issue-types-success
  (fn [{:keys [db]} [_ jira-project-id response]]
    (let [pid-key (keyword jira-project-id)
          types {:requirements (vec (:requirements response []))
                 :issues (vec (:issues response []))}
          project-name (:name response)
          update-mapping #(merge-types-into-mapping % types project-name)
          has-mapping? (some? (get-in db [:entity-mapping :data :project_mappings pid-key]))
          db' (cond-> db
                true (update-in [:entity-mapping :project-issue-types] assoc jira-project-id types)
                true (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) jira-project-id)
                ;; Update both data and saved-data so a name refresh doesn't mark the form dirty
                has-mapping? (update-in [:entity-mapping :data :project_mappings pid-key] update-mapping)
                has-mapping? (update-in [:entity-mapping :saved-data :project_mappings pid-key] update-mapping))]
      {:db db'
       :fx (chain-next-fetch db')})))

(re-frame/reg-event-fx
  ::fetch-project-issue-types-failure
  (fn [{:keys [db]} [_ jira-project-id _response]]
    (let [db' (-> db
                  (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) jira-project-id)
                  (update-in [:entity-mapping :project-types-failed] (fnil conj #{}) jira-project-id))]
      {:db db'
       :fx (into [[:dispatch [::notifications/error2
                              (format-translation (get-translation "bugtracker.integration-settings.error.types-fetch-failed"
                                                                   "Failed to load %{bugtracker} types for one of the projects")
                                                  :bugtracker "Jira")]]]
                 (chain-next-fetch db'))})))

;; -----------------------------------------------------------------------------
;; Sync toggles
;; -----------------------------------------------------------------------------

(re-frame/reg-event-db
 ::toggle-requirements-sync
 (fn [db [_ enabled?]]
   (if enabled?
     (let [current-mappings    (get-in db [:entity-mapping :data :project_mappings] {})
           project-issue-types (get-in db [:entity-mapping :project-issue-types] {})
           issues-enabled?     (get-in db [:entity-mapping :data :issues_sync_enabled] false)
           updated-mappings    (apply-filtered-defaults current-mappings project-issue-types
                                                        :requirements issues-enabled?)]
       (-> db
           (assoc-in [:entity-mapping :data :requirements_sync_enabled] true)
           (assoc-in [:entity-mapping :data :project_mappings] updated-mappings)))
     (assoc-in db [:entity-mapping :data :requirements_sync_enabled] false))))

(re-frame/reg-event-db
 ::toggle-issues-sync
 (fn [db [_ enabled?]]
   (if enabled?
     (let [current-mappings      (get-in db [:entity-mapping :data :project_mappings] {})
           project-issue-types   (get-in db [:entity-mapping :project-issue-types] {})
           requirements-enabled? (get-in db [:entity-mapping :data :requirements_sync_enabled] false)
           updated-mappings      (apply-filtered-defaults current-mappings project-issue-types
                                                           :issues requirements-enabled?)]
       (-> db
           (assoc-in [:entity-mapping :data :issues_sync_enabled] true)
           (assoc-in [:entity-mapping :data :project_mappings] updated-mappings)))
     (assoc-in db [:entity-mapping :data :issues_sync_enabled] false))))

;; -----------------------------------------------------------------------------
;; Project selection
;; -----------------------------------------------------------------------------

(re-frame/reg-event-fx
  ::update-selected-projects
  (fn [{:keys [db]} [_ selected-project-ids]]
    (let [current-mappings (get-in db [:entity-mapping :data :project_mappings] {})
          available-projects (get-in db [:entity-mapping :available-projects] [])
          project-issue-types (get-in db [:entity-mapping :project-issue-types] {})
          kept-mappings (select-keys current-mappings selected-project-ids)
          new-pid-keys (filter #(not (contains? kept-mappings %)) selected-project-ids)
          new-mappings
          (reduce
            (fn [acc pid-key]
              (let [pid-str (name pid-key)
                    project (first (filter #(= (:id %) pid-str) available-projects))
                    types (get project-issue-types pid-str)
                    base {:project_name (:name project)
                          :type_names (if types (build-type-names types) {})}]
                (assoc acc pid-key
                       (if types
                         (assoc base
                                :requirements {:jira_type (ids-for-entity types :requirements)}
                                :issues {:jira_type (ids-for-entity types :issues)})
                         (assoc base
                                :requirements {:jira_type []}
                                :issues {:jira_type []}
                                :_pending_defaults? true)))))
            kept-mappings
            new-pid-keys)
          db' (-> db
                  (assoc-in [:entity-mapping :selected-project-ids] selected-project-ids)
                  (assoc-in [:entity-mapping :data :project_mappings] new-mappings))]
      {:db db'
       :fx (when (empty? (get-in db' [:entity-mapping :project-types-loading] #{}))
             (chain-next-fetch db'))})))

(re-frame/reg-event-db
  ::update-project-mapping
  (fn [db [_ project-id mapping-type field value]]
    (let [k                   (keyword project-id)
          current-mappings    (get-in db [:entity-mapping :data :project_mappings] {})
          updated             (assoc-in current-mappings [k mapping-type field] value)
          project-issue-types (get-in db [:entity-mapping :project-issue-types] {})
          types               (get project-issue-types project-id)
          updated             (if types
                                (assoc-in updated [k :type_names] (build-type-names types))
                                updated)]
      (assoc-in db [:entity-mapping :data :project_mappings] updated))))

(re-frame/reg-event-db
  ::toggle-project-entity-status
  (fn [db [_ project-id entity-type]]
    (let [k                        (keyword project-id)
          current-mappings         (get-in db [:entity-mapping :data :project_mappings] {})
          project-issue-types      (get-in db [:entity-mapping :project-issue-types] {})
          entity-path              [k entity-type]
          current-entity           (get-in current-mappings entity-path)
          is-enabling?             (:disabled current-entity false)
          other-entity-enabled-key (if (= entity-type :requirements) :issues_sync_enabled :requirements_sync_enabled)
          other-entity-enabled?    (get-in db [:entity-mapping :data other-entity-enabled-key] false)
          updated-entity           (if is-enabling?
                                     (apply-filtered-defaults-single-project project-id
                                                                              current-mappings
                                                                              project-issue-types
                                                                              entity-type
                                                                              other-entity-enabled?)
                                     {:jira_type [] :disabled true})]
      (assoc-in db [:entity-mapping :data :project_mappings]
                (assoc-in current-mappings entity-path updated-entity)))))

;; -----------------------------------------------------------------------------
;; General-settings flags
;; -----------------------------------------------------------------------------

(re-frame/reg-event-db
 ::toggle-allow-issue-creation
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :allow_issue_creation] enabled?)))

(re-frame/reg-event-db
 ::toggle-use-pt-id
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :use_pt_id_for_synced_issues] enabled?)))

(re-frame/reg-event-db
 ::toggle-skip-mapping-manual-import
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :skip_mapping_in_manual_import] enabled?)))

;; -----------------------------------------------------------------------------
;; Save / cancel
;; -----------------------------------------------------------------------------

(re-frame/reg-event-fx
 ::save-settings
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id data]} (:entity-mapping db)]
     {:db (-> db
              (assoc-in [:entity-mapping :saving?] true)
              (update :progress conj :save-settings))
      :http-xhrio {:method          :put
                   :uri             (broutes/get-url "integration_settings#update_entity_mapping"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :params          data
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::save-settings-success]
                   :on-failure      [::save-settings-failure]}})))

(re-frame/reg-event-fx
 ::save-settings-success
 (fn [{:keys [db]} [_ response]]
   (let [current-data             (get-in db [:entity-mapping :data])
         current-selected         (get-in db [:entity-mapping :selected-project-ids])
         previously-had-mappings? (seq (get-in db [:entity-mapping :saved-selected-project-ids]))
         jira-user-login-url      (not-empty (:jira_user_login_url response))
         should-reload?           (and (seq current-selected) (not previously-had-mappings?))]
     (when (and should-reload? (not jira-user-login-url))
       (js/location.reload))
     {:db (-> db
              (update :entity-mapping merge
                      {:saving?                    false
                       :saved-data                 current-data
                       :saved-selected-project-ids current-selected
                       :errors                     []
                       :jira-user-login-url        jira-user-login-url
                       :jira-user-reload-on-close? (boolean (and should-reload? jira-user-login-url))})
              (update :progress disj :save-settings))
      :dispatch [::notifications/success2
                 (get-translation "bugtracker.integration-settings.save.success"
                                  "Settings saved successfully")]})))

(re-frame/reg-event-fx
 ::dismiss-jira-user-modal
 (fn [{:keys [db]} [_]]
   (let [needs-sync?                       (some? (get-in db [:entity-mapping :jira-user-login-url]))
         {:keys [project-id integration-id]} (:entity-mapping db)]
     {:db         (update db :entity-mapping merge {:jira-user-login-url nil})
      :http-xhrio (when needs-sync?
                    {:method          :post
                     :uri             (broutes/get-url "integration_settings#trigger_milestones_sync"
                                                       {:project_id project-id
                                                        :id         integration-id})
                     :format          (ajax/json-request-format)
                     :response-format (ajax/json-response-format {:keywords? true})
                     :on-success      [::trigger-sync-success]
                     :on-failure      [::trigger-sync-failure]})})))

(re-frame/reg-event-fx
 ::trigger-sync-success
 (fn [{:keys [db]} _]
   (let [should-reload? (get-in db [:entity-mapping :jira-user-reload-on-close?])]
     (when should-reload? (js/location.reload))
     {:db (update db :entity-mapping merge {:jira-user-reload-on-close? false})})))

(re-frame/reg-event-fx
 ::trigger-sync-failure
 (fn [_ [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "jira-integration-settings.error.trigger-sync-failed"
                                                "Failed to trigger milestones sync"))]
     {:dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-fx
 ::save-settings-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "bugtracker.integration-settings.error.save-failed"
                                                "Failed to save entity mapping"))]
     {:db (-> db
              (update :entity-mapping merge {:saving? false})
              (update :progress disj :save-settings))
      :dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-db
 ::cancel-changes
 (fn [db [_]]
   (let [saved-data     (get-in db [:entity-mapping :saved-data])
         saved-selected (get-in db [:entity-mapping :saved-selected-project-ids])]
     (-> db
         (assoc-in [:entity-mapping :data] saved-data)
         (assoc-in [:entity-mapping :selected-project-ids] saved-selected)))))

;; -----------------------------------------------------------------------------
;; Milestones
;; -----------------------------------------------------------------------------

(re-frame/reg-event-db
 ::toggle-milestones-sync
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :milestones_sync_enabled] enabled?)))

(re-frame/reg-event-db
 ::update-milestones-selected-projects
 (fn [db [_ selected-project-ids]]
   (assoc-in db [:entity-mapping :data :milestones_selected_project_ids] (vec selected-project-ids))))

(re-frame/reg-event-db
 ::toggle-import-completed-sprints
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :import_completed_sprints] enabled?)))
